import React from 'react';
import { AlertTriangle, Calendar, Clock, Users } from 'lucide-react';
import { Button } from './ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import { Badge } from './ui/badge';

const ScheduleConflictModal = ({ 
  open, 
  onClose, 
  conflicts, 
  onOverwrite, 
  onAddAnyway,
  onSkipConflicts 
}) => {
  const hasDuplicates = conflicts?.duplicates && conflicts.duplicates.length > 0;
  const hasHolidays = conflicts?.holidays && conflicts.holidays.length > 0;

  if (!hasDuplicates && !hasHolidays) {
    return null;
  }

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle className="flex items-center space-x-2 text-orange-600">
            <AlertTriangle className="h-5 w-5" />
            <span>Schedule Conflicts Detected</span>
          </DialogTitle>
          <DialogDescription>
            We found some conflicts with your schedule. Please review and choose how to proceed.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 max-h-96 overflow-y-auto">
          {/* Duplicate Shifts Section */}
          {hasDuplicates && (
            <div className="border rounded-lg p-4 bg-yellow-50">
              <h3 className="font-semibold text-gray-900 flex items-center mb-3">
                <Users className="h-4 w-4 mr-2 text-yellow-600" />
                Duplicate Shifts Found ({conflicts.duplicates.length})
              </h3>
              <div className="space-y-3">
                {conflicts.duplicates.map((dup, index) => (
                  <div key={index} className="bg-white rounded-lg p-3 border">
                    <div className="flex items-center justify-between mb-2">
                      <span className="font-medium text-sm">{dup.date}</span>
                      <Badge variant="secondary">{dup.shifts.length} shift(s)</Badge>
                    </div>
                    {dup.shifts.map((shift, idx) => (
                      <div key={idx} className="text-sm text-gray-600 ml-2">
                        • {shift.shift_type} shift: {shift.start_time} - {shift.end_time}
                        {shift.notes && <span className="text-gray-500"> ({shift.notes})</span>}
                      </div>
                    ))}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Holidays Section */}
          {hasHolidays && (
            <div className="border rounded-lg p-4 bg-blue-50">
              <h3 className="font-semibold text-gray-900 flex items-center mb-3">
                <Calendar className="h-4 w-4 mr-2 text-blue-600" />
                Company Holidays Found ({conflicts.holidays.length})
              </h3>
              <div className="space-y-2">
                {conflicts.holidays.map((holiday, index) => (
                  <div key={index} className="bg-white rounded-lg p-3 border">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="font-medium text-sm">{holiday.name}</p>
                        <p className="text-xs text-gray-500">{holiday.date}</p>
                      </div>
                      <Badge variant={holiday.is_closed ? "destructive" : "secondary"}>
                        {holiday.is_closed ? 'Company Closed' : `Skeleton: ${holiday.skeleton_crew_needed}`}
                      </Badge>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        <DialogFooter className="flex flex-col space-y-2">
          <div className="text-sm text-gray-600 mb-2">
            Choose how to handle these conflicts:
          </div>
          
          <div className="flex flex-wrap gap-2 w-full">
            {hasDuplicates && (
              <>
                <Button
                  variant="destructive"
                  onClick={onOverwrite}
                  className="flex-1"
                >
                  Overwrite Existing
                </Button>
                <Button
                  variant="secondary"
                  onClick={onAddAnyway}
                  className="flex-1"
                >
                  Add Anyway
                </Button>
              </>
            )}
            
            {hasHolidays && !hasDuplicates && (
              <>
                <Button
                  variant="secondary"
                  onClick={onSkipConflicts}
                  className="flex-1"
                >
                  Skip Holidays
                </Button>
                <Button
                  variant="destructive"
                  onClick={onAddAnyway}
                  className="flex-1"
                >
                  Override Holidays
                </Button>
              </>
            )}

            {hasDuplicates && hasHolidays && (
              <Button
                variant="secondary"
                onClick={onSkipConflicts}
                className="flex-1"
              >
                Skip All Conflicts
              </Button>
            )}
            
            <Button
              variant="outline"
              onClick={onClose}
              className="flex-1"
            >
              Cancel
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default ScheduleConflictModal;
